// Edit Quote modal: change ONE supplier quote in place via
// PATCH .../items/:itemId/quotes/:quoteId — price, notes, and the offered master
// product (the supplier never changes; the ProductMatcher is scoped to it).
// Delete removes a mistyped quote outright — declining a supplier's offer
// should stay a REJECT so the decision is audited.

import type { QuotationSupplierProduct } from 'apis/restaurant-inquiries';
import {
  RestaurantInquiryItem,
  RestaurantInquiryQuote,
  useDeleteInquiryItemQuoteApi,
  useGetQuotationSupplierProductsApi,
  useUpdateInquiryItemQuoteApi,
} from 'apis/restaurant-inquiries';
import {
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from 'design-system';
import { ProductQuoteHistoryModal } from 'pages/inquiry-processor/components/product-quote-history';
import { PriceLadder } from 'pages/inquiry-processor/components/quote-matcher/price-ladder';
import { ProductMatcher } from 'pages/inquiry-processor/components/quote-matcher/product-matcher';
import { apiErrorMessage } from 'pages/inquiry-processor/utils/inquiry-actions';
import { readSyncPreference, writeSyncPreference } from 'pages/inquiry-processor/utils/price-sync-preference';
import { ladderOf, resolveTier, type PriceTier } from 'pages/inquiry-processor/utils/price-units';
import { quoteMasterProductId, quoteProductLabel } from 'pages/inquiry-processor/utils/quotable';
import { useEffect, useState } from 'react';
import { FiClock } from 'react-icons/fi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  inquiryId: string;
  item: RestaurantInquiryItem;
  quote: RestaurantInquiryQuote;
}

export const EditQuoteModal = ({ isOpen, onClose, inquiryId, item, quote }: Props) => {
  const toast = useToast();
  const updateQuote = useUpdateInquiryItemQuoteApi();
  const deleteQuote = useDeleteInquiryItemQuoteApi();
  const itemName = item.masterProduct?.name || `Item #${item.id}`;
  // Null only on a few old quotes of master-less supplier products: those keep their product.
  const quoteMasterId = quoteMasterProductId(quote);

  const [price, setPrice] = useState('');
  const [notes, setNotes] = useState('');
  const [picked, setPicked] = useState<QuotationSupplierProduct | null>(null);
  // The operator's unit pick, or null while they haven't touched it. Kept
  // separate from the effective rung below so switching to a product with a
  // different packaging ladder re-resolves instead of stranding a stale pick.
  const [pickedUnit, setPickedUnit] = useState<PriceTier | null>(null);
  // "Save to Price tool": the BE upserts supplier_prices with the saved price
  // in the same PATCH. Starts from the sticky preference on every open.
  const [sync, setSync] = useState(false);

  // Re-seed form state when the modal opens or when a NEW server version of
  // this quote lands (updatedAt changes on every PATCH) — keyed on stable
  // fields, not object identity, so unrelated refetches don't clobber typing.
  // The current product is synthesized from the quote's embedded summary so
  // the matcher opens collapsed; live prices are grafted on below.
  useEffect(() => {
    if (!isOpen) return;
    setPrice(quote.price != null ? String(quote.price) : '');
    setNotes(quote.notes || '');
    setPickedUnit(null);
    setSync(readSyncPreference());
    setPicked({
      id: quoteMasterId ?? quote.productId ?? 0,
      nameEn: quoteProductLabel(quote),
      brandEn: quote.product?.brandEn,
      defaultUnit: quote.product?.defaultUnit,
      origin: quote.product?.origin,
      state: quote.product?.state,
      specs: quote.product?.specs,
      // The packaging ladder rides along so the unit picker is populated on the
      // first paint, instead of appearing once the catalog fetch below lands.
      masterProduct: quote.product?.masterProduct,
      // …and so does the master product: `canSync` below reads it.
      masterProductId: quoteMasterId as number,
      supplierId: quote.supplierId,
      supplierPrices: [],
    });
  }, [isOpen, quote.id, quote.updatedAt]);

  // The quote payload carries no prices — enrich the picked row from the
  // supplier's quotable list, searched by the quoted product's family name (a
  // supplier can price far more products than one page holds).
  const searchName = quote.masterProduct?.family?.universalName ?? quote.product?.nameEn ?? undefined;
  const { data: productsData } = useGetQuotationSupplierProductsApi(isOpen ? quote.supplierId : null, searchName);
  const enriched =
    (picked?.masterProductId != null
      ? (productsData?.data ?? []).find((p) => (p.masterProductId ?? p.id) === picked.masterProductId)
      : undefined) ?? picked;

  // The rung the price is read against: the operator's pick when it still names
  // a rung this product defines, else the quote's stored unit, else inferred
  // from the SKU's defaultUnit. Null only when no ladder exists at all.
  const effectiveUnit = enriched
    ? resolveTier(ladderOf(enriched), pickedUnit ?? quote.priceUnit, enriched.defaultUnit)
    : null;

  // The upsert is keyed by (supplier, master product) — nothing to write for an
  // old quote without one, so the checkbox hides itself.
  const canSync = enriched?.masterProductId != null;

  const save = async () => {
    if (!(Number(price) > 0)) {
      toast({ title: 'Enter a price greater than 0', status: 'warning' });
      return;
    }
    if (!picked) {
      toast({ title: 'Pick the offered product', status: 'warning' });
      return;
    }

    // Send only what changed — true PATCH semantics. The unit counts as a
    // change even when the operator didn't touch it: on a quote predating the
    // field, saving records the rung shown in the (pre-filled, visible) picker
    // as an explicit choice instead of leaving it to be re-inferred forever.
    const changes: {
      price?: number;
      priceUnit?: PriceTier;
      notes?: string;
      masterProductId?: number;
      syncSupplierPrice?: boolean;
    } = {};
    if (Number(price) !== quote.price) changes.price = Number(price);
    if (effectiveUnit && effectiveUnit !== quote.priceUnit) changes.priceUnit = effectiveUnit;
    if (notes.trim() !== (quote.notes || '')) changes.notes = notes.trim();
    if (picked.masterProductId != null && picked.masterProductId !== quoteMasterId) {
      changes.masterProductId = picked.masterProductId;
    }
    // Ticked = push the (possibly unchanged) price as the supplier's standing
    // price — a PATCH with only this flag is a legitimate "publish" request.
    if (sync && canSync) changes.syncSupplierPrice = true;

    if (Object.keys(changes).length === 0) {
      // Unticking is the one "change" that leaves `changes` empty (the flag is
      // only ever sent as true) — persist it so the choice sticks.
      if (canSync) writeSyncPreference(sync);
      onClose();
      return;
    }

    let updated: RestaurantInquiryQuote;
    try {
      updated = await updateQuote.mutateAsync({ id: inquiryId, itemId: item.id, quoteId: quote.id, ...changes });
    } catch (err) {
      toast({ title: 'Failed to update quote', description: apiErrorMessage(err), status: 'error' });
      return;
    }
    // The sticky preference may only be updated by a surface that actually
    // offered the checkbox.
    if (canSync) writeSyncPreference(sync);
    // Never report a product switch that didn't happen.
    if (changes.masterProductId != null && updated?.masterProductId !== changes.masterProductId) {
      toast({ title: 'Product was NOT switched', description: 'Please try again.', status: 'error' });
      return;
    }
    toast({
      title: changes.syncSupplierPrice ? 'Quote updated · price saved to the Price tool' : 'Quote updated',
      status: 'success',
    });
    onClose();
  };

  const remove = async () => {
    if (!window.confirm(`Delete this quote for “${itemName}”? This cannot be undone.`)) return;
    try {
      await deleteQuote.mutateAsync({ id: inquiryId, itemId: item.id, quoteId: quote.id });
    } catch (err) {
      toast({ title: 'Failed to delete quote', description: apiErrorMessage(err), status: 'error' });
      return;
    }
    toast({ title: 'Quote deleted', status: 'success' });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: '2xl' }}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Quote — {itemName}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl mb='4' isRequired>
            <FormLabel fontSize='sm'>
              Offered product ({quote.supplier?.name || `Supplier #${quote.supplierId}`})
            </FormLabel>
            <ProductMatcher
              supplierId={quote.supplierId}
              supplierName={quote.supplier?.name}
              item={item}
              value={enriched}
              onChange={setPicked}
            />
          </FormControl>
          {enriched && (
            <FormControl mb='4' isRequired>
              <PriceLadder
                product={enriched}
                price={price}
                onPriceChange={setPrice}
                priceUnit={effectiveUnit}
                onPriceUnitChange={setPickedUnit}
                targetPrice={item.expectedPrice}
                extra={
                  <ProductQuoteHistoryModal
                    masterProductId={enriched.masterProductId}
                    productName={enriched.nameEn || quoteProductLabel(quote)}
                    excludeInquiryId={/^\d+$/.test(inquiryId) ? Number(inquiryId) : undefined}
                    trigger={
                      <IconButton
                        aria-label='Quote history'
                        title='Quote history'
                        size='sm'
                        variant='ghost'
                        icon={<FiClock />}
                      />
                    }
                  />
                }
              />
            </FormControl>
          )}
          {canSync && (
            <FormControl mb='4'>
              <Checkbox size='sm' isChecked={sync} onChange={(e) => setSync(e.target.checked)}>
                <Text as='span' fontSize='sm' fontWeight='600'>
                  Save to Price tool
                </Text>
              </Checkbox>
              <Text fontSize='xs' color='gray.500' pl='6'>
                also becomes {quote.supplier?.name || 'the supplier'}&apos;s standing price for this product
              </Text>
            </FormControl>
          )}
          <FormControl>
            <FormLabel fontSize='sm'>Notes</FormLabel>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder='Delivery, packaging, …' />
          </FormControl>
        </ModalBody>
        <ModalFooter gap='2'>
          <Button
            variant='outline'
            colorScheme='red'
            mr='auto'
            onClick={remove}
            isLoading={deleteQuote.isLoading}
            isDisabled={updateQuote.isLoading}
            loadingText='Deleting…'>
            Delete quote
          </Button>
          <Button variant='outline' onClick={onClose} isDisabled={updateQuote.isLoading || deleteQuote.isLoading}>
            Cancel
          </Button>
          <Button
            colorScheme='red'
            onClick={save}
            isLoading={updateQuote.isLoading}
            isDisabled={deleteQuote.isLoading}
            loadingText='Saving…'>
            Save Changes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
